// Maps a failed POST /login/user response to the message the login screen
// shows. Shared by the identifier form and the legacy colour + PIN form so
// the two methods word their failures the same way.

type T = (key: string, params?: Record<string, string | number>) => string;

export interface LoginErrorBody {
    message?: string;
    code?: string;
    retryAfterSeconds?: number;
    attemptsRemaining?: number;
}

// The IP allow-list gate (403, code "ip_not_allowed") is handled app-wide:
// apiFetch raises NETWORK_BLOCKED_EVENT and App replaces the UI with the
// NetworkBlocked screen, which shows the IP to pass to an admin. The login
// forms must not stack a toast on top of that screen.
export const isNetworkBlocked = (body: LoginErrorBody | null | undefined): boolean =>
    body?.code === "ip_not_allowed";

export const loginErrorMessage = (
    t: T,
    status: number,
    body: LoginErrorBody | null | undefined,
    mode: "identifier" | "colour",
): string => {
    // 423 Locked — per-account (identifier) or per-colour lockout. The BE
    // includes retryAfterSeconds so we can format a friendly window. Goes
    // ahead of the attempts-remaining branch so a locked account is
    // messaged unambiguously rather than counting down attempts that
    // won't be accepted.
    if (status === 423 && typeof body?.retryAfterSeconds === "number") {
        const minutes = Math.max(1, Math.ceil(body.retryAfterSeconds / 60));
        return t(mode === "identifier" ? "login_identifier_locked" : "login_colour_locked", { minutes: String(minutes) });
    }
    if (body?.code === "colour_login_retired") {
        return t("login_colour_retired");
    }
    // 429 from the login throttles (2026-10-20): the identity's own budget
    // (this username / email / colour, from any IP) or the network ceiling.
    if (status === 429 && body?.code === "login_rate_limited_account") {
        return t("login_rate_limited_account");
    }
    if (status === 429 && body?.code === "login_rate_limited_network") {
        return t("login_rate_limited_network");
    }
    if (typeof body?.attemptsRemaining === "number") {
        return body.attemptsRemaining === 1
            ? t("login_one_attempt_remaining")
            : t("login_attempts_remaining", { count: String(body.attemptsRemaining) });
    }
    return body?.message ?? t("server_error");
};
